import { useEffect, useState } from 'react';

import type { AdminUserListItem, AdminUserOab } from '../../../api/adminUsers';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { CopyButton } from '../../../components/ui/copy-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Spinner } from '../../../components/ui/spinner';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { useAdminUser, useUpdateAdminUser } from '../../../hooks/admin/useAdminUsers';
import { classifyError } from '../../../lib/errors';
import { maskCpf } from '../../../lib/formatters';

interface EditUserDialogProps {
  user: AdminUserListItem | null;
  onClose: () => void;
}

function formatOab(oab: AdminUserOab): string {
  return `OAB/${oab.uf} ${oab.numero}`;
}

export function EditUserDialog({ user, onClose }: EditUserDialogProps) {
  const [nome, setNome] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const update = useUpdateAdminUser(user?.id ?? '');
  // CPF e OABs não vêm na listagem — só no detalhe. Carrega ao abrir.
  const detailQuery = useAdminUser(user?.id);
  const detail = detailQuery.data;

  useEffect(() => {
    if (user) {
      setNome(user.nome);
      setIsAdmin(user.is_admin);
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user) return null;

  const dirty = nome.trim() !== user.nome || isAdmin !== user.is_admin;
  const valid = nome.trim().length >= 2;

  const handleSubmit = () => {
    if (!dirty || !valid) return;
    const payload: { nome?: string; is_admin?: boolean } = {};
    if (nome.trim() !== user.nome) payload.nome = nome.trim();
    if (isAdmin !== user.is_admin) payload.is_admin = isAdmin;
    update.mutate(payload, { onSuccess: onClose });
  };

  const errorMessage = update.isError ? classifyError(update.error).message : null;

  return (
    <Dialog open onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Usuário</DialogTitle>
          <DialogDescription>
            O email é a chave de identificação do usuário e não pode ser alterado.
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="principal">
          <TabsList>
            <TabsTrigger value="principal">Principal</TabsTrigger>
            <TabsTrigger value="ids">IDs</TabsTrigger>
          </TabsList>

          <TabsContent value="principal" className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <div className="flex items-center gap-2">
                <Input value={user.email} disabled readOnly />
                <CopyButton text={user.email} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-nome">Nome</Label>
              <Input
                id="edit-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                disabled={update.isPending}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>CPF</Label>
                {detail &&
                  (detail.has_active_certificate ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className="cursor-help border-green-200 bg-green-100 text-green-800 dark:border-green-900/40 dark:bg-green-900/30 dark:text-green-400">
                          Identidade verificada
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>Possui certificado digital ativo</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="cursor-help">
                          Identidade não verificada
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>Sem certificado digital ativo</TooltipContent>
                    </Tooltip>
                  ))}
              </div>
              {detailQuery.isLoading ? (
                <Spinner size={16} label="Carregando CPF" />
              ) : detail?.cpf ? (
                <div className="flex items-center gap-2">
                  <Input value={maskCpf(detail.cpf)} disabled readOnly className="font-mono" />
                  <CopyButton text={detail.cpf} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Não informado.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>OAB</Label>
              {detailQuery.isLoading ? (
                <Spinner size={16} label="Carregando OAB" />
              ) : detail && detail.oabs.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {detail.oabs.map((oab) => (
                    <div
                      key={`${oab.uf}-${oab.numero}`}
                      className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <span className="font-mono">{formatOab(oab)}</span>
                      {oab.tipo !== 'PRINCIPAL' && (
                        <Badge variant="outline">Suplementar</Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhuma OAB cadastrada.</p>
              )}
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label htmlFor="edit-admin" className="cursor-pointer">
                  Administrador
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Promover ou rebaixar o papel admin no Auth0.
                </p>
              </div>
              <Switch
                id="edit-admin"
                checked={isAdmin}
                onCheckedChange={setIsAdmin}
                disabled={update.isPending}
              />
            </div>
          </TabsContent>

          <TabsContent value="ids" className="space-y-4">
            <div className="space-y-2">
              <Label>ID interno</Label>
              <div className="flex items-center gap-2">
                <Input value={user.id} disabled readOnly className="font-mono text-xs" />
                <CopyButton text={user.id} />
              </div>
            </div>

            {user.auth0_id && (
              <div className="space-y-2">
                <Label>Auth0 ID</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={user.auth0_id}
                    disabled
                    readOnly
                    className="font-mono text-xs"
                  />
                  <CopyButton text={user.auth0_id} />
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!dirty || !valid || update.isPending}>
            {update.isPending ? (
              <>
                <Spinner size={16} className="text-inherit" /> Salvando…
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditUserDialog;
